import type { SpecimenGrossingData } from "../pathologist-review/GrossingCenter";
import type {
  DirectCorrectionSpecimenChange,
  DirectCorrectionSpecimenValues,
} from "../../services/patientInfoService";
import type { FragmentCountQualifier } from "../../utils/fragmentCount";

interface GrossCodeOption {
  value: string;
  label: string;
}

interface CaseSpecimen {
  id: string;
  gross?: string | null;
  internalGrossCodeId?: string | null;
  accessioningGrossCodeId?: string | null;
  sourceId?: string | null;
  subsiteId?: string | null;
  subsite?: { name?: string | null } | null;
  collectionMethod?: string | null;
  findings?: string | null;
  material?: string | null;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

function normalizeString(value: unknown): string | null {
  if (value === undefined || value === null || value === "") return null;
  return String(value);
}

function normalizeNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function specimenValuesFromPersisted(
  specimen: CaseSpecimen
): DirectCorrectionSpecimenValues {
  return {
    sourceId: normalizeString(specimen.sourceId),
    subsiteId: normalizeString(specimen.subsiteId),
    collectionMethod: normalizeString(specimen.collectionMethod),
    findings: normalizeString(specimen.findings),
    material: normalizeString(specimen.material),
    fragmentCount: normalizeNumber(specimen.fragmentCount),
    fragmentCountQualifier: specimen.fragmentCountQualifier ?? null,
    internalGrossCodeId: normalizeString(
      specimen.internalGrossCodeId ?? specimen.accessioningGrossCodeId
    ),
    grossDescription: normalizeString(specimen.gross),
  };
}

function specimenValuesFromDraft(
  draft: SpecimenGrossingData
): DirectCorrectionSpecimenValues {
  return {
    sourceId: normalizeString(draft.sourceId),
    subsiteId: normalizeString(draft.subsiteId),
    collectionMethod: normalizeString(draft.collectionMethod),
    findings: normalizeString(draft.findings),
    material: normalizeString(draft.material),
    fragmentCount: normalizeNumber(draft.fragmentCount),
    fragmentCountQualifier: draft.fragmentCountQualifier ?? null,
    internalGrossCodeId: normalizeString(draft.grossCodeId),
    grossDescription: normalizeString(draft.grossDescription),
  };
}

export function buildDirectCorrectionGrossingDataMap(
  specimens: CaseSpecimen[] | undefined,
  grossCodes: GrossCodeOption[]
): Map<string, SpecimenGrossingData> {
  const grossCodeLabels = new Map(
    grossCodes.map((grossCode) => [grossCode.value, grossCode.label])
  );
  return new Map(
    (specimens ?? []).map((specimen) => {
      const grossCodeId =
        specimen.internalGrossCodeId ?? specimen.accessioningGrossCodeId ?? "";
      return [
        specimen.id,
        {
          specimenId: specimen.id,
          grossCode: grossCodeLabels.get(grossCodeId) ?? "",
          grossCodeId,
          grossDescription: specimen.gross ?? "",
          fragmentCount: specimen.fragmentCount ?? null,
          fragmentCountQualifier: specimen.fragmentCountQualifier ?? null,
          sourceId: specimen.sourceId ?? null,
          subsiteId: specimen.subsiteId ?? null,
          subsiteName: specimen.subsite?.name ?? null,
          collectionMethod: specimen.collectionMethod ?? null,
          findings: specimen.findings ?? null,
          material: specimen.material ?? null,
        },
      ];
    })
  );
}

export function buildDirectCorrectionSpecimenChanges(
  specimens: CaseSpecimen[] | undefined,
  grossingDataMap: Map<string, SpecimenGrossingData>
): DirectCorrectionSpecimenChange[] {
  return (specimens ?? []).flatMap((specimen) => {
    const draft = grossingDataMap.get(specimen.id);
    if (!draft) return [];
    const expected = specimenValuesFromPersisted(specimen);
    const proposed = specimenValuesFromDraft(draft);
    const changes = Object.fromEntries(
      Object.entries(proposed).filter(
        ([field, value]) => value !== expected[field as keyof typeof expected]
      )
    ) as Partial<DirectCorrectionSpecimenValues>;
    if (Object.keys(changes).length === 0) return [];
    return [{ specimenId: specimen.id, expected, changes }];
  });
}
